/* eslint-disable import/extensions */
import { Row, Col, Select, Input } from 'antd';
import { useCallback, useEffect, useState, useRef } from 'react';
import {
  Query,
  Builder,
  Utils as QbUtils,
  Config,
  ImmutableTree,
  BuilderProps,
  JsonTree,
} from '@react-awesome-query-builder/antd';
import loadConfig from './config';
import SQLQueryBuilderCustomUtils from './custom_utils/SQLQueryBuilderCustomUtils';
import InterfaceSqlQueryBuilderState from './interfaces/InterfaceSqlQueryBuilderState';
import { useValidation } from './custom_utils/useValidation';
import { queryValidation } from './custom_utils/queryValidation';
import convertToImmutableTreeComplete from './custom_utils/convertToImmutableTreeComplete';
import transformJsonDataQueryToGenerateStateOptimized from './custom_utils/transformJsonDataQueryToGenerateStateOptimized';
import './../sqlQueryBuilder/queryBuilder.css'

const TypeAndQueryCard = ({
  data,
  fields,
  onSave,
  title,
  hideType = false,
}) => {
  const emptyInitValue: JsonTree = { id: QbUtils.uuid(), type: 'group' };
  const [cardType, setThenType] = useState(data.type || 'VALUE');
  const [cardValue, setValue] = useState(data.value || '');
  const loadedConfig = loadConfig('antd', fields);

  const initTree: ImmutableTree = convertToImmutableTreeComplete(
    transformJsonDataQueryToGenerateStateOptimized(
      data?.query || emptyInitValue
    )
  );
  const [state, setState] = useState<InterfaceSqlQueryBuilderState>({
    tree: initTree,
    config: loadedConfig,
    skin: 'antd',
    spelStr: '',
    spelErrors: [] as Array<string>,
    renderBocks: {
      validation: true,
      jsonlogic: true,
      elasticSearch: true,
      mongo: true,
      jsTree: true,
      spel: true,
      strings: true,
      sql: true,
    },
  });

  // Use ref to track previous tree to avoid unnecessary updates
  const prevTreeRef = useRef(state.tree);
  const isInitialMount = useRef(true);
  // Use ref to track when component is mounted
  const isMountedRef = useRef(false);
  // Ref to store the observer instance
  const observerRef = useRef(null);
  // Ref to store interval ID
  const intervalRef = useRef(null);

  // Use ref to track previous saved data to prevent infinite loops
  const prevSavedDataRef = useRef<string>('');

  console.log(cardValue, 'cardValue');
  const { renderValidationBlock } = useValidation(
    state,
    setState,
    cardType,
    cardValue
  );
  const renderBuilder = useCallback(
    (props: BuilderProps) => (
      <div className="query-builder-container">
        <div className="query-builder">
          <Builder {...props} />
        </div>
      </div>
    ),
    []
  );

  const onChange = useCallback(
    (immutableTree: ImmutableTree, config: Config) => {
      setState((prevState) => ({
        ...prevState,
        tree: immutableTree,
        config,
      }));
    },
    []
  );

  useEffect(() => {
    let val: any = '';
    let error: any = '';
    switch (cardType) {
      case 'VALUE':
        const hasValue = cardValue?.trim() !== '';
        val = cardValue;
        error = !hasValue ? 'Field cannot be empty' : '';
        break;
      case 'QUERY':
        const obj = {
          tree: state.tree,
          config: state.config,
        };
        const Error = queryValidation(obj);
        val = SQLQueryBuilderCustomUtils.formatSql(
          QbUtils.sqlFormat(state.tree, state.config)
        );
        error = Error;
        break;
      case 'FIELD':
        const hasField = cardValue?.trim() !== '';
        val = cardValue;
        error = !hasField ? 'Field cannot be empty' : '';
        break;

      default:
        break;
    }

    const newData = {
      value: val,
      query: QbUtils.getTree(state.tree),
      sql: SQLQueryBuilderCustomUtils.formatSql(
        QbUtils.sqlFormat(state.tree, state.config)
      ),
      jsonLogic: QbUtils.jsonLogicFormat(state.tree, state.config),
      type: cardType,
      error: error,
    };

    // Create a stable string representation to compare
    const newDataStr = JSON.stringify(newData);

    // Only call onSave if data actually changed
    if (prevSavedDataRef.current !== newDataStr) {
      prevSavedDataRef.current = newDataStr;
      onSave(newData);
    }
  }, [cardValue, state.tree, state.config, cardType, onSave]);

  useEffect(() => {
    if (hideType) {
      setThenType('QUERY');
    }
  }, [hideType]);

  useEffect(() => {
    // Mark component as mounted
    isMountedRef.current = true;

    // Cleanup on unmount
    return () => {
      isMountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Function to setup conjunctions
  const setupConjunctions = () => {
    if (!isMountedRef.current) return;

    const lastRendered = new WeakMap<Element, string>();

    /**
     * Returns the conjunction type (AND/OR) for a given .group element.
     * Falls back to parent if no buttons are found.
     */
    const getGroupConjunction = (group: Element): string => {
      const buttons = group.querySelectorAll(":scope > .group--header .group--conjunctions button");
      for (const btn of Array.from(buttons)) {
        if (
          btn.classList.contains("ant-btn-primary") ||
          btn.classList.contains("checked") ||
          btn.getAttribute("aria-pressed") === "true"
        ) {
          return btn.textContent?.trim().toUpperCase() || "AND";
        }
      }
      // If no conjunction found locally, inherit from parent
      const parentGroup = group.closest(".group")?.parentElement?.closest(".group");
      return parentGroup ? getGroupConjunction(parentGroup) : "AND";
    };

    /**
     * Change conjunction by clicking the appropriate button in the group header
     */
    const changeConjunction = (group: Element, newConjunction: string) => {
      const conjunctionButtons = group.querySelectorAll(":scope > .group--header .group--conjunctions button");

      // Filter to only AND/OR buttons (exclude NOT)
      const andOrButtons = Array.from(conjunctionButtons).filter(btn => {
        const text = btn.textContent?.trim().toUpperCase();
        return text === 'AND' || text === 'OR';
      });

      // Find and click the button for the selected conjunction
      for (const btn of andOrButtons) {
        const btnText = btn.textContent?.trim().toUpperCase();
        if (btnText === newConjunction) {
          (btn as HTMLButtonElement).click();
          break;
        }
      }
    };

    /**
     * Add conjunction dropdowns between all direct children of a group.
     */
    const updateGroupSeparators = (group: Element) => {
      const conjunction = getGroupConjunction(group);
      if (lastRendered.get(group) === conjunction) return;
      lastRendered.set(group, conjunction);

      const isViewMode = window.location.href.toLowerCase().includes('show');

      const childContainers = group.querySelectorAll(
        ":scope > .group--children > .group-or-rule-container"
      );

      childContainers.forEach((container, idx) => {
        const existing = container.querySelector(".conjunction-separator-container");

        if (idx < childContainers.length - 1) {
          if (!existing) {
            const containerEl = document.createElement("div");
            containerEl.className = "conjunction-separator-container";

            const selectEl = document.createElement("select");

            // ✅ Dynamic class
            selectEl.className = isViewMode
              ? "conjunction-dropdown-disabled"
              : "conjunction-dropdown";

            // ✅ Disable interaction in view mode
            selectEl.disabled = isViewMode;

            selectEl.innerHTML = `
        <option value="AND" ${conjunction === "AND" ? "selected" : ""}>AND</option>
        <option value="OR" ${conjunction === "OR" ? "selected" : ""}>OR</option>
      `;

            // ✅ Only attach events if NOT view mode
            if (!isViewMode) {
              selectEl.addEventListener("mouseenter", () => {
                selectEl.style.transform = "scale(1.02)";
              });

              selectEl.addEventListener("mouseleave", () => {
                selectEl.style.transform = "scale(1)";
              });

              selectEl.addEventListener("focus", () => {
                selectEl.style.boxShadow = "0 0 0 2px rgba(24, 144, 255, 0.2)";
              });

              selectEl.addEventListener("blur", () => {
                selectEl.style.boxShadow = "none";
              });

              selectEl.addEventListener("change", (e) => {
                e.stopPropagation();
                const newValue = (e.target as HTMLSelectElement).value;
                changeConjunction(group, newValue);
              });
            }

            containerEl.appendChild(selectEl);
            container.appendChild(containerEl);
          } else {
            const selectEl = existing.querySelector("select");

            if (selectEl) {
              // ✅ Update value
              if (selectEl.value !== conjunction) {
                selectEl.value = conjunction;
              }

              // ✅ Keep class & disabled state in sync
              selectEl.className = isViewMode
                ? "conjunction-dropdown-disabled"
                : "conjunction-dropdown";

              selectEl.disabled = isViewMode;
            }
          }
        } else if (existing) {
          existing.remove();
        }
      });
    };

    /**
     * Update all visible groups
     */
    const updateAllGroups = () => {
      const groups = document.querySelectorAll(".query-builder .group");
      groups.forEach((group) => {
        updateGroupSeparators(group);
      });
    };

    updateAllGroups();

    // Setup mutation observer for future changes
    const queryBuilder = document.querySelector(".query-builder-container");
    if (!queryBuilder) return;

    // Disconnect existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const observer = new MutationObserver((mutations) => {
      const affectedGroups = new Set<Element>();
      for (const mutation of mutations) {
        if (mutation.target instanceof Element) {
          const group = mutation.target.closest(".group");
          if (group) affectedGroups.add(group);
        }
      }
      if (affectedGroups.size > 0) {
        requestAnimationFrame(() => {
          affectedGroups.forEach((group) => updateGroupSeparators(group));
        });
      }
    });

    observer.observe(queryBuilder, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "aria-pressed"],
    });

    observerRef.current = observer;
  };

  // Effect to initialize conjunctions when the component mounts
  useEffect(() => {
    if (cardType !== 'QUERY') return;

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Set up an interval to check for the Query Builder DOM
    intervalRef.current = setInterval(() => {
      const queryBuilder = document.querySelector(".query-builder .group");
      if (queryBuilder) {
        // DOM is ready, setup conjunctions
        setupConjunctions();
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }, 100);

    // Cleanup after 5 seconds if still not found
    const timeoutId = setTimeout(() => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }, 5000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      clearTimeout(timeoutId);
    };
  }, [cardType]); // Run when cardType changes to 'QUERY'

  // Main useEffect for conjunctions - runs when tree changes
  useEffect(() => {
    if (cardType !== 'QUERY') return;

    if (isInitialMount.current || prevTreeRef.current === state.tree) {
      if (isInitialMount.current) isInitialMount.current = false;
      prevTreeRef.current = state.tree;
      return;
    }
    prevTreeRef.current = state.tree;

    // Setup conjunctions when tree changes
    setupConjunctions();
  }, [state.tree, cardType]);

  return (
    <div className="then-container">
      <section className='then-section-container'>
        <span className={`mutiple-title ${title === 'ELSE' ? 'ml-8' : ''}`}>
          {title}
        </span>
        <Row gutter={16} className='then-section-row'>
          {hideType ? null : (
            <Col span={4}>
              <Select
                className="w-full"
                options={['FIELD', 'VALUE', 'QUERY'].map((label) => ({
                  label,
                  value: label,
                }))}
                defaultValue={cardType}
                onChange={(val) => {
                  setThenType(val);
                }}
              />
            </Col>
          )}

          <Col span={hideType ? 24 : 20}>
            {cardType === 'FIELD' ? (
              <Select
                className="w-full"
                options={Object.keys(fields).map((key) => ({
                  label: key,
                  value: key,
                }))}
                showSearch
                defaultValue={cardValue}
                onChange={(val) => {
                  setValue(val);
                }}
              />
            ) : null}
            {cardType === 'VALUE' ? (
              <Input
                onChange={(e) => setValue(e.target.value)}
                defaultValue={cardValue}
              />
            ) : null}
            {cardType === 'QUERY' ? (
              <Query
                {...state.config}
                value={state.tree}
                onChange={onChange}
                renderBuilder={renderBuilder}
              />
            ) : null}
          </Col>
        </Row>
      </section>

      {/* {renderValidationBlock()} */}
    </div>
  );
};

export default TypeAndQueryCard;